/**
 * Human-readable labels for agent-related enums.
 *
 * Kept separate from AdminAgentsLab.tsx so it can be re-used by any admin
 * page that wants to render a friendly label for an AgentSpecialization /
 * AgentType / ScoreDirection / DataRequirement instead of the raw enum id.
 */
import {
  AgentSpecialization,
  AgentType,
  ScoreDirection,
  DataRequirement,
  SummarizationNeed,
  TaskState,
  EdgeQueueTaskType,
  BullmqJobState,
  VideoRating
} from '../../types/generated/graphql';
import {pcolors} from '../../theme/planapusTheme';

export function humanizeSpecialization(spec: AgentSpecialization | string): string {
  switch (spec) {
    // Education
    case AgentSpecialization.EducationLanguage:
      return 'Education · Language';
    case AgentSpecialization.EducationStem:
      return 'Education · STEM';
    case AgentSpecialization.EducationArts:
      return 'Education · Arts';
    case AgentSpecialization.EducationHistory:
      return 'Education · History';
    case AgentSpecialization.EducationSocialStudies:
      return 'Education · Social Studies';
    case AgentSpecialization.EducationPhysical:
      return 'Education · Physical';

    // Entertainment
    case AgentSpecialization.EntertainmentFiction:
      return 'Entertainment · Fiction';
    case AgentSpecialization.EntertainmentGaming:
      return 'Entertainment · Gaming';
    case AgentSpecialization.EntertainmentMusic:
      return 'Entertainment · Music';
    case AgentSpecialization.EntertainmentComedy:
      return 'Entertainment · Comedy';

    // Safety
    case AgentSpecialization.SafetyOnline:
      return 'Safety · Online';
    case AgentSpecialization.SafetyPhysical:
      return 'Safety · Physical';
    case AgentSpecialization.SafetyEmotional:
      return 'Safety · Emotional';

    // Sensitive topics
    case AgentSpecialization.SensitiveViolence:
      return 'Sensitive · Violence';
    case AgentSpecialization.SensitiveLanguage:
      return 'Sensitive · Language';
    case AgentSpecialization.SensitiveSubstanceUse:
      return 'Sensitive · Substance Use';
    case AgentSpecialization.SensitiveSexuality:
      return 'Sensitive · Sexuality';
    case AgentSpecialization.SensitiveReligion:
      return 'Sensitive · Religion';
    case AgentSpecialization.SensitivePolitics:
      return 'Sensitive · Politics';
    case AgentSpecialization.SensitiveDiscrimination:
      return 'Sensitive · Discrimination';
    case AgentSpecialization.SensitiveMentalHealth:
      return 'Sensitive · Mental Health';

    // Other
    case AgentSpecialization.Appropriateness:
      return 'Appropriateness';
    case AgentSpecialization.DevelopmentalMilestones:
      return 'Developmental Milestones';
    case AgentSpecialization.Engagement:
      return 'Engagement';

    default:
      // Fallback: lowercase + title-case + prettify underscores.
      return String(spec)
        .toLowerCase()
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
}

/** Short tag (no category prefix) for compact chips. */
export function shortSpecialization(spec: AgentSpecialization | string): string {
  const full = humanizeSpecialization(spec);
  const parts = full.split(' · ');
  return parts[parts.length - 1];
}

export function humanizeAgentType(t: AgentType | string): string {
  switch (t) {
    case AgentType.Analysis:
      return 'Analysis';
    case AgentType.Detection:
      return 'Detection';
    default:
      return String(t);
  }
}

export function humanizeScoreDirection(d: ScoreDirection | string): string {
  switch (d) {
    case ScoreDirection.HigherIsBetter:
      return 'Higher is better';
    case ScoreDirection.LowerIsBetter:
      return 'Lower is better';
    default:
      return String(d);
  }
}

export function humanizeDataRequirement(r: DataRequirement | string): string {
  switch (r) {
    case DataRequirement.TranscriptRequired:
      return 'Transcript required';
    case DataRequirement.SummaryRequired:
      return 'Summary required';
    case DataRequirement.MetadataOnly:
      return 'Metadata only';
    default:
      return String(r);
  }
}

export function humanizeSummarizationNeed(s: SummarizationNeed | string | null | undefined): string {
  if (!s) return '—';
  switch (s) {
    case SummarizationNeed.Unified:
      return 'Unified summary';
    case SummarizationNeed.Detailed:
      return 'Detailed summary';
    case SummarizationNeed.RawTranscript:
      return 'Raw transcript';
    case SummarizationNeed.MetadataOnly:
      return 'Metadata only';
    default:
      return String(s);
  }
}

export function humanizeTaskState(s: TaskState | string): string {
  switch (s) {
    case TaskState.Queued: return 'Queued';
    case TaskState.Fetching: return 'Fetching';
    case TaskState.Extracting: return 'Extracting';
    case TaskState.Completed: return 'Completed';
    // Single FAILED state — the *which-stage* and *why* are
    // structured fields (failureStage / failureReason) rendered
    // alongside the pill, not encoded into the state itself.
    case TaskState.Failed: return 'Failed';
    case TaskState.Cancelled: return 'Cancelled';
    default: return String(s);
  }
}

export function describeTaskState(s: TaskState | string): string {
  switch (s) {
    case TaskState.Queued:
      // Wording stays device-neutral — both desktop and mobile can
      // claim, and `claimedByDeviceId` (rendered alongside the
      // pill) names the actual device once one wins the claim.
      return 'Queued — waiting for an edge client to pick it up.';
    case TaskState.Fetching:
      return 'An edge client is currently fetching this from YouTube.';
    case TaskState.Extracting:
      return 'Edge client returned the data; the server is parsing it.';
    case TaskState.Completed:
      return 'Done. Snapshot persisted.';
    case TaskState.Failed:
      return 'Failed — see the failure stage / reason chips for which stage broke and why.';
    case TaskState.Cancelled:
      return 'Cancelled before completion.';
    default:
      return String(s);
  }
}

export function humanizeTaskFailureStage(s: string | null | undefined): string {
  switch (s) {
    case 'FETCH': return 'Fetch';
    case 'EXTRACT': return 'Extract';
    case 'ENQUEUE': return 'Enqueue';
    case 'TIMEOUT': return 'Timeout';
    default: return s ? String(s) : '';
  }
}

export function humanizeTaskFailureReason(s: string | null | undefined): string {
  switch (s) {
    case 'SIGNED_OUT': return 'Signed out';
    case 'COOKIE_EXPIRED': return 'Cookie expired';
    case 'NETWORK': return 'Network';
    case 'YOUTUBE_RATE_LIMIT': return 'Rate-limited';
    case 'YOUTUBE_INVALID_RESPONSE': return 'Bad response';
    case 'PARSER_DRIFT': return 'Parser drift';
    case 'PARSER_REJECTED': return 'Parser rejected';
    case 'WORKER_ERROR': return 'Worker error';
    case 'UNKNOWN': return 'Unknown';
    default: return s ? String(s) : '';
  }
}

export function taskStateColors(s: TaskState | string): {bg: string; color: string} {
  switch (s) {
    case TaskState.Completed: return {bg: pcolors.green[100], color: pcolors.green[700]};
    case TaskState.Failed: return {bg: pcolors.red[100], color: pcolors.red[700]};
    case TaskState.Fetching:
    case TaskState.Extracting: return {bg: pcolors.blue[50], color: pcolors.blue[700]};
    case TaskState.Cancelled: return {bg: pcolors.borderLight, color: pcolors.textMuted};
    default: return {bg: pcolors.borderLight, color: pcolors.textSecondary};
  }
}

export function humanizeEdgeQueueTaskType(t: EdgeQueueTaskType | string): string {
  switch (t) {
    // Drop the "Query " prefix — the panel context already implies
    // these are edge fetches. Shorter labels read cleaner in the
    // task table and bulk-retry tooltips.
    case EdgeQueueTaskType.QueryFeed: return 'Feed';
    case EdgeQueueTaskType.QueryHistory: return 'History';
    case EdgeQueueTaskType.QueryRelated: return 'Related';
    case EdgeQueueTaskType.Transcribe: return 'Transcribe';
    case EdgeQueueTaskType.CorrectiveBlockVideo: return 'Not interested';
    case EdgeQueueTaskType.CorrectiveBlockChannel: return "Don't recommend channel";
    case EdgeQueueTaskType.CorrectiveFollowChannel: return 'Subscribe';
    case EdgeQueueTaskType.CorrectiveUnblockVideo: return 'Undo: Not interested';
    case EdgeQueueTaskType.CorrectiveUnblockChannel: return "Undo: Don't recommend";
    case EdgeQueueTaskType.CorrectiveUnfollowChannel: return 'Undo: Subscribe';
    default:
      return String(t)
        .toLowerCase()
        .replace(/^query_/, '')
        .replace(/^corrective_/, '')
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
}

export function humanizeBullmqJobState(s: BullmqJobState | string): string {
  switch (s) {
    case BullmqJobState.Waiting: return 'Waiting';
    case BullmqJobState.Active: return 'Active';
    case BullmqJobState.Completed: return 'Completed';
    case BullmqJobState.Failed: return 'Failed';
    case BullmqJobState.Delayed: return 'Delayed';
    case BullmqJobState.Paused: return 'Paused';
    default: return String(s);
  }
}

export function bullmqJobStateColors(s: BullmqJobState | string): {bg: string; color: string} {
  switch (s) {
    case BullmqJobState.Completed: return {bg: pcolors.green[100], color: pcolors.green[700]};
    case BullmqJobState.Failed: return {bg: pcolors.red[100], color: pcolors.red[700]};
    case BullmqJobState.Active: return {bg: pcolors.blue[50], color: pcolors.blue[700]};
    case BullmqJobState.Delayed: return {bg: pcolors.amber[100], color: pcolors.amber[700]};
    case BullmqJobState.Paused: return {bg: pcolors.borderLight, color: pcolors.textMuted};
    case BullmqJobState.Waiting:
    default: return {bg: pcolors.borderLight, color: pcolors.textSecondary};
  }
}

// `humanizeAnalysisQueueMessageType` was removed on 2026-04-30. The
// enum values are now tier-prefixed (CONTROL_HEARTBEAT_SESSIONS,
// INGEST_ACK_FEED, SCORING_ANALYZE_VIDEO, EVALUATE_CHANNEL_ACTIONS,
// …) — already self-documenting.
// The BG-jobs view renders the raw enum value in monospace; the
// extra humanization layer was just hiding the actual job name from
// admins debugging Redis/BullMQ.

export function humanizeVideoRating(r: VideoRating | string): string {
  switch (r) {
    case VideoRating.Beneficial: return 'Beneficial';
    case VideoRating.Neutral: return 'Neutral';
    case VideoRating.Harmful: return 'Harmful';
    default: return String(r);
  }
}

/** Deterministic pastel background color from a string (for avatar fallbacks). */
export function colorForString(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 55%, 48%)`;
}

/**
 * Resolve a thumbnail URL the same way AdminAgents does — the build copies
 * agent PNGs into firebase/public/static/img/agents/_v/thumbnails/.
 */
export function resolveAgentThumbnailUrl(
  thumbnails: Array<{ url?: string | null } | null | undefined> | null | undefined
): string | null {
  const url = thumbnails?.[0]?.url;
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('/')) return url;
  return `/static/img/agents/_v/thumbnails/${url}`;
}
